import { NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MigrationSignUpComponent } from '../../components/migration-sign-up/migration-sign-up.component';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, HttpErrorResponse } from '../../services/auth.service';
import { LoadingComponent } from '../../components/loading/loading.component';
import { PAGE_ROUTES } from '../../constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { isEmail } from '../../utils/email.util';
import { catchError, map, Observable, throwError } from 'rxjs';
import { showSnackBar } from '../../utils/snackBar.ulti';
import { AuthkitService } from '../../services/authKit.service';
import { extractErrors } from '../../utils/extracErrors.util';

type CheckUserOrEmailMapType = {
  isExistedUser: boolean;
  isMigratedUser: boolean;
};

type MigrateStateType = {
  username: string;
  oldPassword: string;
};

@Component({
  selector: 'app-migrate',
  standalone: true,
  imports: [ReactiveFormsModule, NgIf, MigrationSignUpComponent, LoadingComponent],
  templateUrl: './migrate.component.html',
  styleUrl: './migrate.component.scss'
})
export class MigrateComponent implements OnInit {
  protected email?: string | null;
  protected token?: string | null;
  protected oldPassword?: string | null;
  protected username?: string | null;
  protected isLoading = false;

  private readonly _migratedAccountNoti = 'This account has been migrated. Log in to get started.';
  private readonly _state: MigrateStateType;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _authService: AuthService,
    private _snackBar: MatSnackBar,
    private _translate: TranslateService,
    private _authKitService: AuthkitService
  ) {
    const navigation = this._router.getCurrentNavigation();
    this._state = navigation?.extras.state as MigrateStateType;
  }

  ngOnInit(): void {
    this._route.queryParamMap.subscribe(async (params) => {
      const token = decodeURIComponent(params.get('token') || '');
      let email = decodeURIComponent(params.get('email') || '');

      if (email.includes(' ')) {
        email = email.replaceAll(' ', '+');
      }

      // validate email
      if (email && !isEmail(email)) {
        this.isLoading = false;
        showSnackBar({ type: 'error', message: 'Email is invalid', snackBar: this._snackBar, translate: this._translate });

        return;
      }

      // migrate with email + token
      if (email && token) {
        await this._handlePreAuthentication(email, token);

        return;
      }

      if (this._state) {
        // migrate with username + oldPassword
        const { username, oldPassword } = this._state;

        if (username && oldPassword) {
          this.email = email;
          this.username = this._state.username;
          this.oldPassword = this._state.oldPassword;
        }
      } else {
        // redirect to home page if not enough information (oldPassword + username or email + token)
        this._router.navigate([PAGE_ROUTES.HOME], { queryParams: email ? { email } : undefined });
      }
    });
  }

  private async _handlePreAuthentication(email: string, token: string): Promise<void> {
    this.isLoading = true;

    await this._authKitService.init({ redirectUri: window.location.origin });

    const checkUsernameOrEmail: Observable<CheckUserOrEmailMapType> = this._authService.checkUsernameOrEmail(email).pipe(
      map((result) => ({
        isExistedUser: result?.userExists,
        isMigratedUser: result?.isUserMigrated
      })),
      catchError((error: HttpErrorResponse) => {
        this._extractErrors(error);

        return throwError(() => error);
      })
    );

    this._handleMigrationWithToken<CheckUserOrEmailMapType>({ observable: checkUsernameOrEmail, token, email });
  }

  private _handleMigrationWithToken<T extends CheckUserOrEmailMapType>({
    observable,
    email,
    token
  }: {
    observable: Observable<T>;
    email: string;
    token: string;
  }): void {
    observable.subscribe(async (result) => {
      const { isExistedUser, isMigratedUser } = result;

      if (!isExistedUser) {
        this._onUserNotExisted(email);

        return;
      }

      if (isMigratedUser) {
        this._onUserMigrated(email);

        return;
      }

      this._onUserNotMigrated(email, token);
    });
  }

  private _onUserNotExisted(email: string) {
    this._router.navigate([PAGE_ROUTES.NOT_FOUND], {
      state: { returnTo: window.location.pathname, queryParams: { email } }
    });
  }

  private _onUserMigrated(email: string) {
    showSnackBar({ type: 'error', message: this._migratedAccountNoti, snackBar: this._snackBar, translate: this._translate });

    // Signin with workos instance
    this._authKitService.signIn({ loginHint: email });
  }

  private _onUserNotMigrated(email: string, token: string) {
    this.email = email;
    this.token = token;

    this.isLoading = false;
  }

  private _extractErrors(apiResponse: HttpErrorResponse): void {
    this.isLoading = false;
    const { status } = apiResponse ?? {};

    extractErrors({ status, snackBar: this._snackBar, translate: this._translate });
  }
}
